import React from "react";
import "./HowItWorks.css";
import Layout from "../PageLayout/Layout";
import StatsBar from "../shared/StatsBar";

const IconUnify = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="9" y="3" width="6" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.6" />
    <rect x="3" y="16" width="6" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.6" />
    <rect x="15" y="16" width="6" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12 8v4M12 12H6v4M12 12h6v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const IconAutomate = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="4" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
    <rect x="14" y="13" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M10 7.5h4.5V13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconDecide = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3.5l7 3v5.2c0 4.3-2.9 8.2-7 9.3-4.1-1.1-7-5-7-9.3V6.5l7-3z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path d="M9.2 12.1l1.9 1.9 3.8-3.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DESKTOP_STEPS = [
  {
    num: "01",
    tag: "UNIFY",
    tagClass: "is-teal",
    title: "One intelligence layer",
    desc: "Connects fragmented systems into a single, structured source of organizational knowledge.",
    icon: <IconUnify />,
  },
  {
    num: "02",
    tag: "AUTOMATE",
    tagClass: "is-purple",
    title: "Workflow orchestration",
    desc: "Routes, prioritizes, and executes operational work automatically — not just answers questions.",
    icon: <IconAutomate />,
  },
  {
    num: "03",
    tag: "DECIDE",
    tagClass: "is-blue",
    title: "Explainable by default",
    desc: "Every recommendation shows its evidence, confidence, and reasoning — built for audit from day one.",
    icon: <IconDecide />,
  },
];

export default function HowItWorks() {
  return (
    <>
      {/* Mobile UI — unchanged */}
      <section className="howitworks-mobile">
        <Layout>
          <div className="container">
            <p className="section-label">
              <img src="/icons/bolt.svg" alt="bolt Icon" />
              How It Works
            </p>
            <h2 className="steps-heading">From Connection to Compliance. In Three Steps.</h2>
            <p className="steps-subtext">
              Highwatch unifies your enterprise systems, understands every policy and process, and turns questions into compliant, auditable actions, in seconds.
            </p>

            <div className="steps-grid">
              <div className="step-card">
                <div className="step-inner-border">
                  <div className="step-image-box">
                    <img src="/images/How it works1.png" alt="Connect" className="step-image" />
                  </div>
                  <h3 className="step-title">Connect</h3>
                  <p className="step-desc">
                    Integrate Highwatch AI with your enterprise tools — Salesforce, ServiceNow, SharePoint, SAP, and more. Your documents, workflows, and data stay securely in place while Highwatch builds a unified intelligence layer on top.
                  </p>
                </div>
              </div>

              <div className="step-card">
                <div className="step-inner-border">
                  <div className="step-image-box">
                    <img src="/images/How it works2.png" alt="Ask" className="step-image" />
                  </div>
                  <h3 className="step-title">Understand</h3>
                  <p className="step-desc">
                    Highwatch AI interprets your organization’s context — policies, procedures, and structured data — using domain-specific AI models. It identifies relationships, applies compliance logic, and prepares verified answers ready for execution.
                  </p>
                </div>
              </div>

              <div className="step-card">
                <div className="step-inner-border">
                  <div className="step-image-box">
                    <img src="/images/How it works3.png" alt="Act" className="step-image" />
                  </div>
                  <h3 className="step-title">Execute</h3>
                  <p className="step-desc">
                    Take instant action across your connected systems — update a record, trigger a workflow, or generate an audit-ready summary. Every action is logged, versioned, and traceable, ensuring full compliance from request to completion.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Layout>
      </section>

      {/* Desktop Figma How it Works + stats */}
      <section className="hw-how">
        <div className="hw-how__header">
          <p className="hw-how__eyebrow">HOW IT WORKS</p>
          <h2>
            Unifies knowledge. Automates
            <br />
            operations. Drives decisions.
          </h2>
        </div>

        <div className="hw-how__grid">
          {DESKTOP_STEPS.map((step) => (
            <article key={step.num} className={`hw-how-card ${step.tagClass}`}>
              <div className="hw-how-card__top">
                <span className="hw-how-card__num">{step.num}</span>
                <span className={`hw-how-card__tag ${step.tagClass}`}>{step.tag}</span>
              </div>
              <div className={`hw-how-card__icon ${step.tagClass}`} aria-hidden="true">
                {step.icon}
              </div>
              <h3>{step.title}</h3>
              <p>{step.desc}</p>
            </article>
          ))}
        </div>
      </section>

      <StatsBar />
    </>
  );
}
